import { GroupBase, StylesConfig } from "react-select";
import { ISelectOption } from "types/gaiaComponents/select";

// Helper functions to determine colors based on state
const getTextColor = (state: { isSelected?: boolean; isDisabled?: boolean; isFocused?: boolean }): string => {
    if (state.isSelected) return 'var(--ga-color-text-body)';
    if (state.isDisabled) return 'var(--ga-color-text-disable-selected)';
    if (state.isFocused) return 'var(--ga-color-text-action-hover)';
    return 'var(--ga-color-text-action)';
};

const getBorderColor = (
    state: { isDisabled?: boolean; isFocused?: boolean },
    invalid?: boolean
): string => {
    if (state.isDisabled) return 'var(--ga-color-border-disabled)';
    if (invalid) return 'var(--ga-color-border-error)';
    if (state.isFocused) return 'var(--ga-color-border-focus)';
    return 'var(--ga-color-border-primary)';
};

const getBackgroundColor = (
    state: { isDisabled?: boolean; hasValue?: boolean },
    invalid?: boolean
): string => {
    if (state.isDisabled) return 'var(--ga-color-surface-disabled)';
    if (invalid && !state.hasValue) return 'var(--ga-color-surface-error)';
    return 'var(--ga-color-surface-primary)';
};

const getBoxShadow = (state: { isFocused?: boolean }, invalid?: boolean): string => {
    if (!state.isFocused && !invalid) return 'none';
    const color = invalid ? 'var(--ga-color-border-error)' : 'var(--ga-color-border-focus)';
    return `0 0 0 1px ${color}`;
};

const getHoverBorderColor = (
    state: { isFocused?: boolean },
    invalid?: boolean
): string => {
    if (invalid) return 'var(--ga-color-border-error)';
    if (state.isFocused) return 'var(--ga-color-border-focus)';
    return 'var(--ga-color-border-action-hover)';
};

const getHoverBackgroundColor = (
    state: { isDisabled?: boolean; hasValue?: boolean },
    invalid: boolean | undefined,
    providedBgColor: any
): string => {
    if (state.isDisabled) return 'var(--ga-color-surface-disabled)';
    if (state.hasValue && !invalid) return 'var(--ga-color-surface-action-hover-2)';
    return providedBgColor;
};

const getOptionBackgroundColor = (state: { isSelected?: boolean; isDisabled?: boolean; isFocused?: boolean }): string => {
    if (state.isSelected) return 'var(--ga-color-surface-success)';
    if (state.isDisabled) return 'var(--ga-color-surface-disabled)';
    if (state.isFocused) return 'var(--ga-color-surface-action-hover-2)';
    return 'transparent';
};


const getOptionHoverBackgroundColor = (state: { isDisabled?: boolean }): string => {
    return state.isDisabled
        ? 'var(--ga-color-surface-disabled)'
        : 'var(--ga-color-surface-action-hover-2)';
};

const getOptionHoverTextColor = (state: { isDisabled?: boolean }): string => {
    return state.isDisabled
        ? 'var(--ga-color-text-disable-selected)'
        : 'var(--ga-color-text-action-hover)';
};

export const getGaiaStyles = <
    Option = ISelectOption,
    IsMulti extends boolean = false,
    Group extends GroupBase<Option> = GroupBase<Option>
>(invalid?: boolean): StylesConfig<Option, IsMulti, Group> => ({
    control: (provided, state) => ({
        ...provided,
        minHeight: '40px',
        height: 'auto',
        color: getTextColor(state),
        borderRadius: 'var(--ga-radius)',
        borderColor: getBorderColor(state, invalid),
        backgroundColor: getBackgroundColor(state, invalid),
        boxShadow: getBoxShadow(state, invalid),
        fontSize: 'calc(var(--ga-text-md-font-size) * var(--ga-base-scaling-factor, 1))',
        padding: '4px 12px',
        cursor: state.isDisabled ? 'not-allowed' : 'pointer',
        display: 'flex',
        alignItems: 'center',
        pointerEvents: state.isDisabled ? 'none' : 'auto',
        '&:hover': {
            borderColor: getHoverBorderColor(state, invalid),
            backgroundColor: getHoverBackgroundColor(state, invalid, provided.backgroundColor),
        },
    }),
    valueContainer: (provided) => ({
        ...provided,
        padding: '0 8px',
        minHeight: '26px',
        gap: '4px',
        flexWrap: 'wrap',
        display: 'flex',
        alignItems: 'center',
    }),
    input: (provided, state) => ({
        ...provided,
        margin: '0 4px',
        padding: 0,
        color: getTextColor(state),
        fontSize: 'inherit',
    }),
    placeholder: (provided) => ({
        ...provided,
        color: 'var(--ga-color-text-disabled)',
        margin: '0 4px',
        lineHeight: 'normal',
    }),
    singleValue: (provided, state) => ({
        ...provided,
        color: state.isDisabled
            ? 'var(--ga-color-text-disable-selected)'
            : 'var(--ga-color-text-primary)',
        margin: '0 4px',
        lineHeight: 'normal',
    }),
    multiValue: (provided) => ({
        ...provided,
        backgroundColor: 'var(--ga-color-surface-primary)',
        border: '1px solid var(--ga-color-border-action)',
        borderRadius: 'var(--ga-radius-round)',
        display: 'flex',
        alignItems: 'center',
        paddingTop: '3px',
        paddingBottom: '3px',
    }),
    multiValueLabel: (provided) => ({
        ...provided,
        color: 'var(--ga-color-text-primary)',
        paddingLeft: '6px',
        paddingRight: '6px',
        paddingTop: 0,
        paddingBottom: 0,
        fontSize: 'inherit',
        lineHeight: 'normal',
        borderRight: '1px solid var(--ga-color-border-disabled)',
        borderRadius: 0,
    }),
    multiValueRemove: (provided) => ({
        ...provided,
        color: 'var(--ga-color-icon-action)',
        cursor: 'pointer',
        paddingTop: 0,
        paddingBottom: 0,
        '&:hover': {
            backgroundColor: 'transparent',
            color: 'var(--ga-color-text-error)',
        },
    }),
    menu: (provided) => ({
        ...provided,
        backgroundColor: 'var(--ga-color-surface-primary)',
        border: '1px solid var(--ga-color-border-primary)',
        borderRadius: 'var(--ga-radius)',
        boxShadow: 'none',
        marginTop: '8px',
        zIndex: 9999,
        fontSize: 'calc(var(--ga-text-md-font-size) * var(--ga-base-scaling-factor, 1))',
        width: 'auto',
        minWidth: '50%',
        maxWidth: '100%',
    }),
    menuPortal: (provided) => ({
        ...provided,
        zIndex: 9999999, // Higher than modal z-index (99999) to appear above when portaled to document.body
    }),
    menuList: (provided) => ({
        ...provided,
        padding: '0',
        maxHeight: '324px', // max-h-81 = 324px
        overflow: 'auto',
        borderRadius: 'var(--ga-radius)',
    }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: getOptionBackgroundColor(state),
        color: getTextColor(state),
        fontWeight: state.isSelected ? 600 : 'normal',
        cursor: state.isDisabled ? 'default' : 'pointer',
        padding: '8px 12px',
        fontSize: 'calc(var(--ga-text-md-font-size) * var(--ga-base-scaling-factor, 1))',
        minHeight: '36px',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        '&:first-child': {
            borderTopLeftRadius: 'var(--ga-radius)',
            borderTopRightRadius: 'var(--ga-radius)',
        },
        '&:last-child': {
            borderBottomLeftRadius: 'var(--ga-radius)',
            borderBottomRightRadius: 'var(--ga-radius)',
        },
        '&:hover': {
            backgroundColor: getOptionHoverBackgroundColor(state),
            color: getOptionHoverTextColor(state),
        },
        '&:active': {
            backgroundColor: getOptionHoverBackgroundColor(state),
        },
    }),
});

